import { InteractionStep } from "./types";
import {
    LucideIcon,
    ListChecks,
    CircleHelp,
    Settings,
    SlidersHorizontal,
} from "lucide-react";
import {
    AiChatInteractionContent,
    AudioInteractionContent,
    CloseQuestionInteractionContent,
    ClozeInteractionContent,
    FormInteractionContent,
    LinkInteractionContent,
    MatchInteractionContent,
    OpenQuestionInteractionContent,
    RatingInteractionContent,
    VideoInteraction,
    VideoInteractionType,
} from "@yedalabs/schema";

export const interactionModalSteps: {
    id: InteractionStep;
    keyName: string;
    defaultValue: string;
    uniqueTextPerType?: Partial<
        Record<VideoInteractionType, { keyName: string; defaultValue: string }>
    >;
    showForTypes?: VideoInteractionType[];
    icon: LucideIcon;
}[] = [
    {
        id: "time-and-type",
        keyName: "interaction-modal.sidebar.time-and-type",
        defaultValue: "זמן וסוג",
        icon: SlidersHorizontal,
    },
    {
        id: "question",
        keyName: "interaction-modal.sidebar.question",
        defaultValue: "שאלה",
        uniqueTextPerType: {
            rating: {
                keyName: "interaction-modal.sidebar.rating",
                defaultValue: "דירוג",
            },
            form: {
                keyName: "interaction-modal.sidebar.form",
                defaultValue: "טופס",
            },
            link: {
                keyName: "interaction-modal.sidebar.link",
                defaultValue: "לינק",
            },
            audio: {
                keyName: "interaction-modal.sidebar.audio",
                defaultValue: "כותרת",
            },
            cloze: {
                keyName: "interaction-modal.sidebar.cloze",
                defaultValue: "כותרת",
            },
            match: {
                keyName: "interaction-modal.sidebar.match",
                defaultValue: "כותרת",
            },
        },
        icon: CircleHelp,
    },
    {
        id: "answers",
        keyName: "interaction-modal.sidebar.answers",
        defaultValue: "תשובות",
        showForTypes: ["open", "close", "audio", "cloze", "match", "aiChat"],
        uniqueTextPerType: {
            audio: {
                keyName: "interaction-modal.sidebar.answers.audio",
                defaultValue: "תוכן",
            },
            cloze: {
                keyName: "interaction-modal.sidebar.answers.cloze",
                defaultValue: "טקסט",
            },
            match: {
                keyName: "interaction-modal.sidebar.answers.match",
                defaultValue: "קלפים",
            },
        },
        icon: ListChecks,
    },
    {
        id: "settings",
        keyName: "interaction-modal.sidebar.settings",
        defaultValue: "הגדרות",
        showForTypes: [
            "open",
            "close",
            "form",
            "rating",
            "audio",
            "cloze",
            "match",
            "aiChat",
        ],
        icon: Settings,
    },
];

export const interactionTypeOptions: {
    id: VideoInteractionType;
    keyName: string;
    defaultValue: string;
}[] = [
    {
        id: "open",
        keyName: "interaction-modal.select-type.open",
        defaultValue: "שאלה פתוחה",
    },
    {
        id: "close",
        keyName: "interaction-modal.select-type.closed",
        defaultValue: "שאלה סגורה",
    },
    {
        id: "form",
        keyName: "interaction-modal.select-type.form",
        defaultValue: "טופס",
    },
    {
        id: "rating",
        keyName: "interaction-modal.select-type.rating",
        defaultValue: "דירוג",
    },
    {
        id: "link",
        keyName: "interaction-modal.select-type.link",
        defaultValue: "לינק",
    },
    {
        id: "audio",
        keyName: "interaction-modal.select-type.audio",
        defaultValue: "אודיו",
    },
    {
        id: "cloze",
        keyName: "interaction-modal.select-type.cloze",
        defaultValue: "השלמת משפטים",
    },
    {
        id: "match",
        keyName: "interaction-modal.select-type.match",
        defaultValue: "התאמת קלפים",
    },
    {
        id: "aiChat",
        keyName: "interaction-modal.select-type.ai-chat",
        defaultValue: "שיחה עם AI",
    },
];

export const defaultContentForType = (
    type: VideoInteractionType
): VideoInteraction["content"] => {
    switch (type) {
        case "open":
            return {
                question: "",
                answers: [],
                timeLimit: null,
                attemptLimit: 1,
                rewindTimestamp: null,
                allowSkipping: true,
                hideCorrectAnswer: false,
            } satisfies OpenQuestionInteractionContent;
        case "close":
            return {
                question: "",
                answers: [],
                timeLimit: null,
                attemptLimit: 1,
                rewindTimestamp: null,
                allowSkipping: true,
                hideCorrectAnswer: false,
            } satisfies CloseQuestionInteractionContent;
        case "rating":
            return {
                question: "",
                allowSkipping: true,
            } satisfies RatingInteractionContent;
        case "form":
            return {
                question: "",
                fields: [],
                allowSkipping: true,
            } satisfies FormInteractionContent;
        case "link":
            return {
                question: "",
                url: "",
            } satisfies LinkInteractionContent;
        case "audio":
            return {
                question: "",
                sentence: "",
                language: "en",
                attemptLimit: 1,
                allowSkipping: true,
            } satisfies AudioInteractionContent;
        case "cloze":
            return {
                question: "",
                text: "",
                allowSkipping: true,
            } satisfies ClozeInteractionContent;
        case "match":
            return {
                question: "",
                pairs: [],
                allowSkipping: true,
            } satisfies MatchInteractionContent;
        case "aiChat":
            return {
                question: "",
                answers: [],
                allowSkipping: true,
            } satisfies AiChatInteractionContent;
    }
};
